import React, { createContext, useContext, useState, useMemo } from 'react'
import { Movement } from '../../interfaces/inventory'
import { useBatchMovements } from '../../hooks/useBatchMovements'

interface MovimientosFilters {
  operationFilter?: string // Tipo (ENTRY, OUT)
  almacenFilter?: string // Almacén (ID)
  userFilter?: string // Usuario (ID)
  categoryFilter?: string // Categoría
  entryDateFrom?: string // Fecha desde (YYYY-MM-DD)
  entryDateTo?: string // Fecha hasta (YYYY-MM-DD)
}

interface MovimientosContextType {
  movimientos: Movement[]
  allMovimientos: Movement[]
  filters: MovimientosFilters
  setFilters: React.Dispatch<React.SetStateAction<MovimientosFilters>>
  clearFilters: () => void
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  totalItems: number
  totalPages: number
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  refetch: () => void
}

const MovimientosContext = createContext<MovimientosContextType | undefined>(
  undefined
)

interface MovimientosProviderProps {
  children: React.ReactNode
  loteId: string | number | null
}

export function MovimientosProvider({
  children,
  loteId,
}: MovimientosProviderProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState<MovimientosFilters>({})

  // Convert loteId to number if it's a string
  const batchId = typeof loteId === 'string' ? parseInt(loteId) : loteId

  // Use the new batch movements hook with updated filter names
  const {
    data: batchMovementsData,
    isLoading: isLoadingMovements,
    isError: isErrorMovements,
    refetch: refetchMovements,
    isFetching: isFetchingMovements,
  } = useBatchMovements({
    batchId,
    operations:
      filters.operationFilter && filters.operationFilter !== 'all'
        ? [filters.operationFilter]
        : undefined,
    categories:
      filters.categoryFilter && filters.categoryFilter !== 'all'
        ? [filters.categoryFilter]
        : undefined,
    areaIds:
      filters.almacenFilter && filters.almacenFilter !== 'all'
        ? [parseInt(filters.almacenFilter)]
        : undefined,
    dateFrom: filters.entryDateFrom,
    dateTo: filters.entryDateTo,
    userId:
      filters.userFilter && filters.userFilter !== 'all'
        ? parseInt(filters.userFilter)
        : undefined,
    page: currentPage,
    limit: pageSize,
    enabled: batchId !== null,
  })

  // Transform API movements into the expected format
  const allMovimientos = useMemo(() => {
    if (!batchMovementsData?.movements) return []

    return batchMovementsData.movements.map((movement, index) => {
      const movementObject: Movement = {
        // Core API fields
        id: movement.id || `movement-${index}`,
        quantity: movement.quantity || 0,
        balance: movement.balance || 0,
        operation: movement.operation || 'ENTRY',
        status: movement.status || 'APPROVED',
        category: movement.category || '',
        area: movement.area || { id: 0, name: 'Sin área' },
        movedTo: movement.movedTo || null,
        movedBy: movement.movedBy || {
          id: 0,
          email: '',
          username: '',
          displayName: '',
        },
        createdAt: movement.createdAt || new Date().toISOString(),
        batchId: movement.batchId || 0,

        // Legacy compatibility fields
        type:
          movement.operation === 'ENTRY'
            ? 'ENTRADA'
            : movement.operation === 'OUT'
              ? 'SALIDA'
              : movement.operation === 'TRANSFER'
                ? 'TRANSFERENCIA'
                : 'AJUSTE',
        dateTime: movement.createdAt || new Date().toISOString(),
        loteId: loteId?.toString(),
      }

      return movementObject
    })
  }, [batchMovementsData, loteId])

  const clearFilters = () => {
    setFilters({})
  }

  const totalItems = batchMovementsData?.total || 0
  const totalPages = Math.ceil(totalItems / pageSize)

  const paginatedMovimientos = allMovimientos

  const handleSetPageSize = (size: number) => {
    setPageSize(size)
    // Reset to first page when changing page size (if not already on page 1)
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const contextValue: MovimientosContextType = {
    movimientos: paginatedMovimientos,
    allMovimientos: allMovimientos,
    filters,
    setFilters,
    clearFilters,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize: handleSetPageSize,
    totalItems,
    totalPages,
    isLoading: isLoadingMovements || false,
    isFetching: isFetchingMovements || false,
    isError: isErrorMovements || false,
    refetch:
      refetchMovements ||
      (() => {
        // No-op fallback
      }),
  }

  return (
    <MovimientosContext.Provider value={contextValue}>
      {children}
    </MovimientosContext.Provider>
  )
}

export function useMovimientos() {
  const context = useContext(MovimientosContext)
  if (context === undefined) {
    throw new Error('useMovimientos must be used within a MovimientosProvider')
  }
  return context
}
